/**
 * Vanity accessory constants and rules.
 * Shared between cloud functions and web app.
 *
 * Mirror, sconce, and lighting placement rules extracted from
 * functions/features/tools/designAutomation/rules/vanityAccessoryRules.js.
 */

const { INCHES_TO_METERS, VANITY_DIMENSIONS } = require('./constants');

// --- Mirror ---
const MIN_GAP_ABOVE_COUNTER = 0.254; // 10 inches
const MIN_GAP_BETWEEN_MIRRORS = 0.1; // 4 inches
const MIRROR_MIN_SIZE = 0.3; // 12 inches
const MIRROR_WALL_STANDOFF = 0.0127; // 0.5 inches

// Canonical persisted modelName for the full-wall procedural mirror.
// Single source of truth — every site that needs to recognize the
// full-wall variant routes through `isFullWallProceduralMirror`.
const FULL_WIDTH_PROCEDURAL_MIRROR_NAME = 'Full-width procedural mirror';

function isFullWallProceduralMirror(mirror) {
  return (
    !!mirror?.isProcedural &&
    mirror?.modelName === FULL_WIDTH_PROCEDURAL_MIRROR_NAME
  );
}

// --- Sconces ---
const MIN_EDGE_GAP_MIRROR_TO_SCONCE = 0.0254; // 1 inch minimum edge-to-edge gap
const MAX_SPACING_FOR_OUTER_ONLY = 1.0668; // 3.5 feet

// Mirror-to-sconce default gaps. Single-vanity defaults to 4" so a
// single pair looks balanced on a wider wall; double-vanity defaults
// to 3" with a 1" floor so case-based layouts fit within tighter sink
// spacing. Used by sconcePlacement.js.
const DEFAULT_GAP_MIRROR_TO_SCONCE_SINGLE = 0.1; // 4 inches
const DEFAULT_GAP_MIRROR_TO_SCONCE_DOUBLE = 0.076; // 3 inches
const MIN_GAP_MIRROR_TO_SCONCE_DOUBLE = 0.025; // 1 inch

// Sentinel passed in place of a measured side space when the obstruction
// is "unbounded" — larger than any rule cares about. Keeps gap-shrinking
// branches in `calculateSingleVanitySconceOffsets` simple (`< 999`).
const UNBOUNDED_WALL_SPACE = 999;

// --- Sconce height ---
const MIN_SCONCE_HEIGHT = 60 * INCHES_TO_METERS; // 5'0" above floor
const MAX_SCONCE_HEIGHT = 74 * INCHES_TO_METERS; // 6'2" above floor
const DEFAULT_SCONCE_HEIGHT = 66 * INCHES_TO_METERS; // 5'6" above floor

// --- Lighting space thresholds ---
const MIN_SIDE_SPACE_FOR_SCONCE = 0.2032; // 8 inches each side
const MIN_OVERHEAD_SPACE = 0.2032; // 8 inches above mirror

// --- Embedded-sconce layout (over the mirror, between sinks) ---
// Used by `tryEmbeddedSconces` in `lightingPlacement.js` for full-wall
// procedural mirrors where sconces sit *inside* the mirror footprint.
const DEFAULT_SCONCE_DISTANCE_FROM_SINK = 0.15; // 6 inches
const MIN_SCONCE_DISTANCE_FROM_SINK = 0.1; // 4 inches floor when crowded
const EMBEDDED_SCONCE_MARGIN_FROM_MIRROR_EDGE = 0.05; // 2 inches

// --- Embedded-sconce gap rules (only applied inside tryEmbeddedSconces) ---
// Per-gap minima between adjacent sconces. Flanking sconces keep their
// existing behavior — these rules are scoped to embedded only so saved
// non-procedural-mirror layouts don't shift.
//
// MIN_USABLE_MIRROR_OVER_SINK: minimum inner-edge gap between adjacent
// sconces when the gap COVERS A SINK. Leaves usable mirror at face level.
// MIN_INNER_SCONCE_GAP: minimum inner-edge gap when the gap covers EMPTY
// inter-sink countertop (4-sconce variant only). Aesthetic floor.
const MIN_USABLE_MIRROR_OVER_SINK = 20 * INCHES_TO_METERS; // 0.508 m
const MIN_INNER_SCONCE_GAP = 6 * INCHES_TO_METERS; // 0.1524 m

// Same-type swap reposition notification threshold. Lateral shifts below
// this (1") on any sconce stay silent; shifts at or above surface a
// "Repositioned sconces to accommodate new model size" notification.
const SIGNIFICANT_SCONCE_SHIFT = 0.0254; // 1 inch

// Tentative-pass seed when no model is in hand yet. The tentative pass
// returns a maxWidth budget; the catalog query uses that to filter
// upfront so we don't fetch a too-wide sconce and bounce.
const ESTIMATED_SCONCE_WIDTH_FALLBACK = 0.1; // 4 inches

// --- Accessory-to-accessory gaps ---
// Distance between mirror top and overhead wall-light bottom. Applied
// uniformly across optimize, reposition, and resize paths.
const OVERHEAD_GAP_ABOVE_MIRROR = 0.05; // 2 inches

// --- Ceiling margin (reserved) ---
// Reserved for a follow-up feature PR that shrinks `availableHeight`
// inside `calculateMirrorWallSpace` by this amount to leave room for
// crown molding at the top of the wall. Not yet referenced.
// eslint-disable-next-line no-unused-vars
const CEILING_MARGIN_FOR_CROWN_MOLDING = 0.1143; // 4.5 inches

/**
 * Mirror center Y that respects the min gap above the counter top.
 * `heightAboveCounter` is the model's preferred clearance (usually
 * `VANITY_DIMENSIONS.mirror.heightAboveCounter`); falls back to the
 * MIN_GAP_ABOVE_COUNTER floor. Shared by creation and reposition so
 * both paths place a mirror of given height at the same Y relative
 * to the counter top.
 */
function computeMirrorCenterY(
  countertopTopY,
  mirrorHeight,
  heightAboveCounter
) {
  const gap = Math.max(
    heightAboveCounter ?? MIN_GAP_ABOVE_COUNTER,
    MIN_GAP_ABOVE_COUNTER
  );
  return countertopTopY + gap + mirrorHeight / 2;
}

/**
 * Overhead wall-light center Y — sits OVERHEAD_GAP_ABOVE_MIRROR above
 * the mirror's top edge. Shared by creation and reposition so both
 * paths place the light with the same gap.
 */
function computeOverheadLightCenterY(
  mirrorCenterY,
  mirrorHeight,
  overheadHeight
) {
  const mirrorTopY = mirrorCenterY + mirrorHeight / 2;
  return mirrorTopY + OVERHEAD_GAP_ABOVE_MIRROR + overheadHeight / 2;
}

/**
 * Get sconce center Y, clamped to acceptable height range.
 */
function getSconceCenterY(mirrorCenterY, floorY) {
  const mirrorHeightAboveFloor = mirrorCenterY - floorY;
  if (
    mirrorHeightAboveFloor >= MIN_SCONCE_HEIGHT &&
    mirrorHeightAboveFloor <= MAX_SCONCE_HEIGHT
  ) {
    return mirrorCenterY;
  }
  return floorY + DEFAULT_SCONCE_HEIGHT;
}

/**
 * Sconce Y center for a vanity mirror, clamped to the accepted height
 * band. If no mirror was placed, uses the Y a standard-size mirror
 * WOULD sit at above the counter — the principled fallback so
 * creation, restyle, and reposition paths converge on one rule.
 */
function computeVanitySconceCenterY({
  mirror,
  countertopTopY,
  floorY,
  fallbackMirrorDims = VANITY_DIMENSIONS.mirror,
}) {
  const syntheticY =
    countertopTopY +
    fallbackMirrorDims.heightAboveCounter +
    fallbackMirrorDims.height / 2;
  const rawY = mirror?.center?.y ?? syntheticY;
  return getSconceCenterY(rawY, floorY);
}

/**
 * Calculate max mirror dimensions based on available space.
 */
function calculateMaxMirrorDimensions(options) {
  const {
    availableHeight,
    minGapAboveCounter = MIN_GAP_ABOVE_COUNTER,
    distanceToLeftEdge,
    distanceToRightEdge,
    distanceToOtherSink = null,
    minGapBetweenMirrors = MIN_GAP_BETWEEN_MIRRORS,
  } = options;

  const maxHeight = Math.max(0, availableHeight - minGapAboveCounter);
  const maxWidthFromEdges =
    2 * Math.min(distanceToLeftEdge, distanceToRightEdge);

  let maxWidth = maxWidthFromEdges;
  if (distanceToOtherSink !== null) {
    const maxWidthFromOtherSink = distanceToOtherSink - minGapBetweenMirrors;
    maxWidth = Math.min(maxWidth, maxWidthFromOtherSink);
  }

  maxWidth = Math.max(MIRROR_MIN_SIZE, maxWidth);
  const finalMaxHeight = Math.max(MIRROR_MIN_SIZE, maxHeight);

  return { maxWidth, maxHeight: finalMaxHeight };
}

/**
 * Match a single-sink vanity's mirror width to its cabinet width — the
 * mirror follows the cabinet in BOTH directions (narrows a too-wide
 * catalog pick, widens a too-narrow one), bounded above by the wall space
 * available for the mirror (`maxWallSpaceWidth`, from
 * `calculateMirrorWallSpace`) and below by `MIRROR_MIN_SIZE`.
 *
 * Double-sink vanities use their own per-sink sizing
 * (`calculateDoubleVanityLayout`'s case-based inches table) and never
 * call this.
 */
/**
 * Along-wall width of the overhead light bar over a given mirror: a fixed
 * share of the mirror, capped so a wide mirror doesn't grow an absurd bar.
 * Shared by the cabinet and standalone-sink accessory paths so the two
 * cannot drift.
 */
const OVERHEAD_BAR_MIRROR_FRACTION = 0.8;
const OVERHEAD_BAR_MAX_WIDTH = 0.6; // ~24"

function overheadBarWidth(mirrorWidth) {
  return Math.min(
    (mirrorWidth || 0) * OVERHEAD_BAR_MIRROR_FRACTION,
    OVERHEAD_BAR_MAX_WIDTH
  );
}

function calculateMatchedMirrorWidth(vanityWidth, maxWallSpaceWidth) {
  const upperBound = Number.isFinite(maxWallSpaceWidth)
    ? maxWallSpaceWidth
    : Infinity;
  return Math.max(MIRROR_MIN_SIZE, Math.min(vanityWidth, upperBound));
}

/**
 * Re-derive a single-sink vanity's matched mirror width — and stamp it
 * onto a cloned `selectedMirror` — from a mirror-wall-space measurement
 * (`leftSpace`/`rightSpace`, either `null` for "unbounded" per
 * `calculateMirrorWallSpace`'s contract) and the cabinet's CURRENT width.
 *
 * Single source for the "match, bounded by wall space" math so it can't
 * drift between the two call sites that need it: the initial selection in
 * `selectMirrorAndLighting.js`, and the same-pass re-derivation in
 * `optimize_vanity` (vanity.js) after a collision/depth-driven cabinet
 * resize changes `vanityWidth` — the initial selection ran against the
 * PRE-resize width, so anything it computed is stale once the cabinet
 * resize block runs.
 *
 * Pure; caller decides whether/when to call it and supplies its own
 * `leftSpace`/`rightSpace` (fresh or reused).
 */
/**
 * Narrowest a single-sink mirror goes to make room for flanking sconces
 * inside the vanity span. Below this the sconces lose, not the mirror.
 */
const SINGLE_SINK_MIRROR_MIN_WITH_SCONCES = 24 * 0.0254;
/** A sconce keeps this much clear on its OUTER side (wall corner, fixture). */
const MIN_SCONCE_OUTER_CLEARANCE = 3 * 0.0254;

/** Catalog GLBs have fixed dimensions; only procedural / scalable ones size. */
function isResizableAccessory(model) {
  if (!model) return false;
  if (model.isProcedural || model.isScalable) return true;
  return !model.id && !model.url; // an anonymous spec, not a product
}

/**
 * The width a single-sink vanity's mirror SHOULD be: the cabinet width,
 * bounded by wall space, minus room for flanking sconces when they are
 * wanted (`reserveForSconces: { sconceWidth, gap }`). Sconces flank the
 * MIRROR and need, per side, the larger of MIN_SIDE_SPACE_FOR_SCONCE and
 * sconce + mirror gap + MIN_SCONCE_OUTER_CLEARANCE; what the wall beyond
 * the cabinet doesn't provide comes out of the mirror, symmetrically (the
 * mirror stays centred over the sink), down to
 * SINGLE_SINK_MIRROR_MIN_WITH_SCONCES — below that the reservation is
 * dropped (the sconces lose, not the mirror).
 */
function computeMirrorTargetWidth({
  vanityWidth,
  leftSpace,
  rightSpace,
  reserveForSconces = null,
  maxAdjacentWidth = undefined,
}) {
  const minSideSpace = Math.min(leftSpace ?? Infinity, rightSpace ?? Infinity);
  const alongWallWidth = Number.isFinite(minSideSpace)
    ? vanityWidth + 2 * minSideSpace
    : undefined;
  // `leftSpace`/`rightSpace` measure ALONG the parent wall; a PERPENDICULAR
  // wall bounds the mirror in a direction they cannot see. Sizing against the
  // first while `checkAdjacentWallOverlap` judges by the second is what left a
  // vanity with no mirror at all — the catalog pick was rejected, the
  // fixed-width fallback was rejected too, and post-processing deleted it.
  // One budget serves both.
  const maxWallSpaceWidth = Number.isFinite(maxAdjacentWidth)
    ? Math.min(alongWallWidth ?? Infinity, maxAdjacentWidth)
    : alongWallWidth;
  let targetWidth = calculateMatchedMirrorWidth(vanityWidth, maxWallSpaceWidth);
  let reservedForSconces = false;
  if (reserveForSconces) {
    const need = Math.max(
      MIN_SIDE_SPACE_FOR_SCONCE,
      (reserveForSconces.sconceWidth || 0) +
        (reserveForSconces.gap || 0) +
        MIN_SCONCE_OUTER_CLEARANCE
    );
    const insideLeft = Math.max(0, need - (leftSpace ?? Infinity));
    const insideRight = Math.max(0, need - (rightSpace ?? Infinity));
    const candidate = targetWidth - 2 * Math.max(insideLeft, insideRight);
    if (candidate >= SINGLE_SINK_MIRROR_MIN_WITH_SCONCES) {
      targetWidth = candidate;
      reservedForSconces = true;
    }
  }
  return { targetWidth, reservedForSconces };
}

/**
 * Apply the target width to the selected mirror — but ONLY if the mirror
 * can actually change size. A catalog product keeps its real dimensions
 * (nothing stretches a GLB on screen, and the data must not claim
 * otherwise); the caller uses `targetWidth` to QUERY for a product that
 * fits instead. Returns the real `mirrorWidth` either way.
 */
function rematchMirrorToVanity({
  selectedMirror,
  vanityWidth,
  leftSpace,
  rightSpace,
  reserveForSconces = null,
  maxAdjacentWidth = undefined,
}) {
  const { targetWidth, reservedForSconces } = computeMirrorTargetWidth({
    vanityWidth,
    leftSpace,
    rightSpace,
    reserveForSconces,
    maxAdjacentWidth,
  });
  if (isResizableAccessory(selectedMirror)) {
    return {
      mirrorWidth: targetWidth,
      targetWidth,
      reservedForSconces,
      selectedMirror: selectedMirror
        ? { ...selectedMirror, widthMeters: targetWidth }
        : selectedMirror,
    };
  }
  const realWidth = selectedMirror?.widthMeters;
  return {
    mirrorWidth: Number.isFinite(realWidth) ? realWidth : targetWidth,
    targetWidth,
    reservedForSconces,
    selectedMirror,
  };
}

/**
 * Can a preset (user-chosen) mirror be placed once per sink on a double
 * vanity? Products are never resized, so a preset wider than the per-sink
 * bound (`calculateDoubleVanityLayout`'s case table) would overlap its
 * twin; the caller then falls back to the sized catalog query.
 */
function presetMirrorFitsPerSink(preset, maxWidthPerSink) {
  if (!preset) return false;
  if (!Number.isFinite(maxWidthPerSink)) return true;
  const w = preset.widthMeters;
  return !Number.isFinite(w) || w <= maxWidthPerSink + 1e-9;
}

/**
 * Fit an existing vanity accessory (mirror or light bar) to its cabinet:
 * set its width to `targetWidth` and re-center it on the cabinet's
 * along-wall centerline while PRESERVING its wall-perpendicular seating
 * and height. Pure — returns `{ center, dimensions }` patches (x/z only;
 * y untouched), or `null` when the accessory is already within `epsilon`
 * of the target (width and lateral offset), so callers can no-op.
 *
 * The scan pipeline needs this because bathroom post-processing keeps a
 * pre-existing (scanned or previously-built) mirror rather than
 * rebuilding it, so `buildProceduralMirror`'s own sizing never runs for
 * it — an off-catalog 30" mirror over a 21.6" cabinet survives every
 * reprocess otherwise.
 *
 * `lateralCenter` overrides WHERE along the wall the accessory centres,
 * defaulting to the cabinet's own centre. A double vanity's mirrors centre
 * on their own basins (see `planVanityMirrors`), not on the cabinet — but
 * they must still keep the cabinet's wall-perpendicular seating, so the
 * perpendicular offset is always measured against `cabinetCenter`
 * regardless. Passing only `cabinetCenter` reproduces the original
 * single-mirror behaviour exactly.
 */
function fitVanityAccessoryToCabinet({
  accessory,
  cabinetCenter,
  cabinetRotationY,
  targetWidth,
  lateralCenter,
  epsilon = 0.001,
}) {
  const ry = cabinetRotationY ?? 0;
  // Wall-perpendicular axis n and along-wall (lateral) axis u for the
  // vanity's rotation, matching the wallProjector convention used by the
  // procedural builders.
  const n = { x: Math.sin(ry), z: Math.cos(ry) };
  const u = { x: Math.cos(ry), z: -Math.sin(ry) };
  const dx = accessory.center.x - cabinetCenter.x;
  const dz = accessory.center.z - cabinetCenter.z;
  const perp = dx * n.x + dz * n.z;
  // Along-wall offset of the requested centre from the cabinet's own.
  const anchor = lateralCenter ?? cabinetCenter;
  const lateral =
    (anchor.x - cabinetCenter.x) * u.x + (anchor.z - cabinetCenter.z) * u.z;
  const center = {
    ...accessory.center,
    x: cabinetCenter.x + perp * n.x + lateral * u.x,
    z: cabinetCenter.z + perp * n.z + lateral * u.z,
  };
  const widthDelta = Math.abs(accessory.dimensions.x - targetWidth);
  const lateralDelta = Math.hypot(
    center.x - accessory.center.x,
    center.z - accessory.center.z
  );
  if (widthDelta <= epsilon && lateralDelta <= epsilon) return null;
  return {
    center,
    dimensions: { ...accessory.dimensions, x: targetWidth },
  };
}

/**
 * Where an item of `itemWidth` centres when it wants to sit over `target`
 * but must stay inside a span of `spanWidth` centred on `spanCenter`.
 *
 * The one place the "centre it on the basin, but don't let it hang off the
 * end" rule is implemented. A vanity's accessories are laid out over a basin
 * that is NOT reliably centred in its own cabinet — the layout pipeline
 * slides it along the wall so its standing clearance misses a toilet or a
 * door (`clearanceOffset`, `shared-lib/bathroom-analyzer/conversion.js`) —
 * so every accessory that belongs over the basin has to make the same
 * decision, and they must not drift apart.
 *
 * Returns `target` itself when it already fits, `spanCenter` when there is
 * no target, no projector, or no room to move, and a point on the segment
 * between them otherwise. Only the ALONG-WALL component is meaningful:
 * callers re-derive perpendicular seating and height from the wall or from
 * the accessory's own anchor.
 *
 * Note the degenerate case is the CORRECT answer, not a giveup: an item as
 * wide as its span has nowhere to go and already covers the target wherever
 * the target sits.
 *
 * Pure. `projectOntoWall` is affine (`makeWallProjector`), so a difference
 * of two projections is a true along-wall distance.
 */
function clampLateralWithinSpan({
  target,
  spanCenter,
  spanWidth,
  itemWidth,
  projectOntoWall,
}) {
  if (!target || !spanCenter || typeof projectOntoWall !== 'function') {
    return spanCenter;
  }
  const maxShift = Math.max(0, (spanWidth - itemWidth) / 2);
  const delta = projectOntoWall(target) - projectOntoWall(spanCenter);
  if (maxShift === 0 || delta === 0) return spanCenter;
  if (Math.abs(delta) <= maxShift) return target;

  const t = maxShift / Math.abs(delta);
  return {
    x: spanCenter.x + (target.x - spanCenter.x) * t,
    y: spanCenter.y + (target.y - spanCenter.y) * t,
    z: spanCenter.z + (target.z - spanCenter.z) * t,
  };
}

module.exports = {
  clampLateralWithinSpan,
  MIN_GAP_ABOVE_COUNTER,
  MIN_GAP_BETWEEN_MIRRORS,
  MIRROR_MIN_SIZE,
  MIRROR_WALL_STANDOFF,
  FULL_WIDTH_PROCEDURAL_MIRROR_NAME,
  MIN_EDGE_GAP_MIRROR_TO_SCONCE,
  MAX_SPACING_FOR_OUTER_ONLY,
  DEFAULT_GAP_MIRROR_TO_SCONCE_SINGLE,
  DEFAULT_GAP_MIRROR_TO_SCONCE_DOUBLE,
  MIN_GAP_MIRROR_TO_SCONCE_DOUBLE,
  UNBOUNDED_WALL_SPACE,
  MIN_SCONCE_HEIGHT,
  MAX_SCONCE_HEIGHT,
  DEFAULT_SCONCE_HEIGHT,
  MIN_SIDE_SPACE_FOR_SCONCE,
  SINGLE_SINK_MIRROR_MIN_WITH_SCONCES,
  MIN_SCONCE_OUTER_CLEARANCE,
  computeMirrorTargetWidth,
  presetMirrorFitsPerSink,
  isResizableAccessory,
  MIN_OVERHEAD_SPACE,
  DEFAULT_SCONCE_DISTANCE_FROM_SINK,
  MIN_SCONCE_DISTANCE_FROM_SINK,
  EMBEDDED_SCONCE_MARGIN_FROM_MIRROR_EDGE,
  MIN_USABLE_MIRROR_OVER_SINK,
  MIN_INNER_SCONCE_GAP,
  SIGNIFICANT_SCONCE_SHIFT,
  ESTIMATED_SCONCE_WIDTH_FALLBACK,
  OVERHEAD_GAP_ABOVE_MIRROR,
  CEILING_MARGIN_FOR_CROWN_MOLDING,
  calculateMaxMirrorDimensions,
  calculateMatchedMirrorWidth,
  rematchMirrorToVanity,
  fitVanityAccessoryToCabinet,
  overheadBarWidth,
  isFullWallProceduralMirror,
  getSconceCenterY,
  computeVanitySconceCenterY,
  computeMirrorCenterY,
  computeOverheadLightCenterY,
};
